import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { Box, Paper, Typography } from "@mui/material";
import type { ColDef, RowClassRules } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment from "moment-timezone";
import { useMemo } from "react";
import type { IncentiveRow, DayTotals } from "../../models/IncentiveRow";
import { IncPercentPill } from "../../shared/components/IncPercentPill";
import {
	DEFAULT_INCENTIVE_THRESHOLDS,
	ROW_TINTS,
	SURFACE,
	TEXT,
	type SixZoneThresholds,
} from "../../constants/colors";
import { SHIFT_CONFIG_COLORS } from "@/shared/components/KPICard";

interface DayMeta {
	dayLabel: string;
	isFirstOfDay: boolean;
	dayRowSpan: number;
}

/** Formats "2026-05-28" → "Wednesday" without timezone shift */
const getDayLabel = (iso: string): string =>
	moment(iso.substring(0, 10), "YYYY-MM-DD").format("dddd");

/** Formats "2026-05-28" → "05/28" without timezone shift */
const fmtShortDate = (iso: string): string => {
	const [, m, d] = iso.substring(0, 10).split("-");
	return `${m}/${d}`;
};

type Props = {
	dailyRows: IncentiveRow[];
	dayTotals: DayTotals[];
	thresholds?: SixZoneThresholds;
};

export const DailySummaryGrid = ({
	dailyRows,
	dayTotals,
	thresholds,
}: Props) => {
	const { defaultColDef } = useGridDefinitions({});
	const incThresholds = thresholds ?? DEFAULT_INCENTIVE_THRESHOLDS;

	const totalsMap = useMemo(() => {
		const map = new Map<string, DayTotals>();
		for (const dt of dayTotals) {
			map.set(dt.ProductionDate, dt);
		}
		return map;
	}, [dayTotals]);

	const dayMetaByShiftId = useMemo(() => {
		const map = new Map<number, DayMeta>();
		const rowCountByDate = new Map<string, number>();
		const firstShiftIdByDate = new Map<string, number>();

		dailyRows.forEach((row, index) => {
			rowCountByDate.set(
				row.ProductionDate,
				(rowCountByDate.get(row.ProductionDate) ?? 0) + 1,
			);
			if (
				index === 0 ||
				dailyRows[index - 1]?.ProductionDate !== row.ProductionDate
			) {
				firstShiftIdByDate.set(row.ProductionDate, row.ShiftId);
			}
		});

		dailyRows.forEach((row) => {
			const isFirstRow =
				firstShiftIdByDate.get(row.ProductionDate) === row.ShiftId;
			map.set(row.ShiftId, {
				dayLabel: getDayLabel(row.ProductionDate),
				isFirstOfDay: isFirstRow,
				dayRowSpan: isFirstRow
					? (rowCountByDate.get(row.ProductionDate) ?? 1)
					: 1,
			});
		});

		return map;
	}, [dailyRows]);

	/** Set of ProductionDates that contain a current shift (for spanned cell tinting) */
	const currentDates = useMemo(() => {
		const set = new Set<string>();
		for (const r of dailyRows) {
			if (r.IsCurrentShift) set.add(r.ProductionDate);
		}
		return set;
	}, [dailyRows]);

	/** Set of ProductionDates where ALL shifts are future (for avg pill coloring) */
	const allFutureDates = useMemo(() => {
		const dateShifts = new Map<string, boolean[]>();
		for (const r of dailyRows) {
			if (!dateShifts.has(r.ProductionDate))
				dateShifts.set(r.ProductionDate, []);
			dateShifts.get(r.ProductionDate)!.push(r.IsFutureShift);
		}
		const set = new Set<string>();
		for (const [date, flags] of dateShifts) {
			if (flags.every(Boolean)) set.add(date);
		}
		return set;
	}, [dailyRows]);

	const columnDefs = useMemo(
		(): ColDef<IncentiveRow>[] => [
			{
				headerName: "Day",
				width: 110,
				valueGetter: (params) => {
					const row = params.data;
					if (!row) return "";
					return dayMetaByShiftId.get(row.ShiftId)?.dayLabel ?? "";
				},
				rowSpan: (params) => {
					const row = params.data;
					if (!row) return 1;
					return dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1;
				},
				cellClassRules: {
					"ag-cell-row-span": (params) => {
						const row = params.data;
						return (
							!!row && (dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1) > 1
						);
					},
					"incentive-cell-day-border": (params) => {
						const row = params.data;
						return (
							!!row && (dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1) > 1
						);
					},
					"incentive-row-current": (params) => {
						const row = params.data;
						return (
							!!row &&
							(dayMetaByShiftId.get(row.ShiftId)?.isFirstOfDay ?? false) &&
							currentDates.has(row.ProductionDate)
						);
					},
				},
				cellRenderer: (params: { data: IncentiveRow }) => {
					const dayMeta = dayMetaByShiftId.get(params.data.ShiftId);
					if (!dayMeta?.isFirstOfDay) return null;
					return (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								lineHeight: 1.3,
							}}
						>
							<span style={{ fontWeight: 600 }}>{dayMeta.dayLabel}</span>
							<span style={{ fontSize: "0.7rem", color: TEXT.muted }}>
								{fmtShortDate(params.data.ProductionDate)}
							</span>
						</div>
					);
				},
				cellStyle: (params) =>
					!!params.data &&
					(dayMetaByShiftId.get(params.data.ShiftId)?.isFirstOfDay ?? false)
						? { display: "flex", alignItems: "center" }
						: undefined,
			},
			{
				headerName: "Crew",
				field: "Crew",
				width: 60,
				cellStyle: { textAlign: "center" } as const,
			},
			{
				headerName: "Shift",
				field: "Shift",
				width: 60,
				cellStyle: { textAlign: "center" } as const,
			},
			{
				headerName: "Target Tons",
				field: "DisplayedTarget",
				width: 85,
				cellStyle: { textAlign: "center" } as const,
				cellRenderer: (params: { data: IncentiveRow }) => {
					const row = params.data;
					return (
						<span style={{ position: "relative" }}>
							{row.DisplayedTarget ? row.DisplayedTarget.toFixed(0) : "—"}
						</span>
					);
				},
			},
			{
				headerName: "Finished Tons",
				field: "DisplayedFinished",
				width: 90,
				cellStyle: { textAlign: "right" } as const,
				cellRenderer: (params: { data: IncentiveRow }) => {
					const row = params.data;
					if (row.IsFutureShift) {
						return <span style={{ color: TEXT.disabled }}>—</span>;
					}
					return (
						<span>
							{row.DisplayedFinished != null
								? row.DisplayedFinished.toFixed(0)
								: "—"}
						</span>
					);
				},
			},
			{
				headerName: "Adj.",
				field: "Adjustment",
				width: 55,
				cellStyle: { textAlign: "right" } as const,
				valueFormatter: (params) => {
					const v = params.value;
					if (v === 0 || v == null) return "—";
					return v > 0 ? `+${v}` : String(v);
				},
			},
			{
				headerName: "Hours",
				field: "HoursWorked",
				width: 50,
				cellStyle: { textAlign: "right" } as const,
				valueFormatter: (params) => (params.value ? String(params.value) : "—"),
			},
			{
				headerName: "Inc%",
				field: "IncPercent",
				width: 90,
				cellRenderer: (params: {
					value: number | null;
					data: IncentiveRow;
				}) => (
					<IncPercentPill
						value={params.value}
						thresholds={incThresholds}
						isFuture={params.data?.IsFutureShift}
					/>
				),
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				} as const,
			},
			{
				headerName: "Avg Inc%",
				width: 100,
				rowSpan: (params) => {
					const row = params.data;
					if (!row) return 1;
					return dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1;
				},
				cellClassRules: {
					"ag-cell-row-span": (params) => {
						const row = params.data;
						return (
							!!row && (dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1) > 1
						);
					},
					"incentive-cell-day-border": (params) => {
						const row = params.data;
						return (
							!!row && (dayMetaByShiftId.get(row.ShiftId)?.dayRowSpan ?? 1) > 1
						);
					},
					"incentive-row-current": (params) => {
						const row = params.data;
						return (
							!!row &&
							(dayMetaByShiftId.get(row.ShiftId)?.isFirstOfDay ?? false) &&
							currentDates.has(row.ProductionDate)
						);
					},
				},
				cellRenderer: (params: { data: IncentiveRow }) => {
					const dayMeta = dayMetaByShiftId.get(params.data.ShiftId);
					if (!dayMeta?.isFirstOfDay) return null;

					const t = totalsMap.get(params.data.ProductionDate);
					return (
						<IncPercentPill
							value={t?.AvgIncPercent ?? null}
							thresholds={incThresholds}
							isFuture={allFutureDates.has(params.data.ProductionDate)}
						/>
					);
				},
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				} as const,
			},
		],
		[totalsMap, dayMetaByShiftId, incThresholds, currentDates, allFutureDates],
	);

	const rowClassRules = useMemo<RowClassRules<IncentiveRow>>(
		() => ({
			"incentive-row-current": (params) => params.data?.IsCurrentShift === true,
		}),
		[],
	);

	return (
		<Paper
			elevation={2}
			sx={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				"& .incentive-row-current": {
					backgroundColor: `${ROW_TINTS.current} !important`,
				},
				"& .ag-cell-row-span:not(.incentive-row-current)": {
					backgroundColor: "inherit",
				},
				"& .incentive-cell-day-border": {
					borderBottom: "1px solid var(--ag-border-color) !important",
				},
			}}
		>
			<Box
				sx={{
					px: 2,
					py: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
					Daily Summary
				</Typography>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<IncentiveRow>
					rowData={dailyRows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					suppressRowTransform={true}
					rowHeight={34}
					headerHeight={34}
					animateRows
					getRowId={(params) => String(params.data.ShiftId)}
					rowClassRules={rowClassRules}
					domLayout="normal"
					suppressCellFocus
					suppressContextMenu
				/>
			</Box>
			<Box
				sx={{
					px: 2,
					py: 1.5,
					borderTop: "1px solid",
					borderColor: "divider",
					display: "flex",
					flexDirection: "column",
					gap: 0.75,
					fontSize: "0.75rem",
					color: "text.secondary",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Box
							sx={{
								width: 14,
								height: 14,
								borderRadius: "2px",
								backgroundColor: ROW_TINTS.current,
								border: `1px solid ${SURFACE.border}`,
								flexShrink: 0,
							}}
						/>
						<span>Current shift in progress</span>
					</Box>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						mt: 0.5,
						flexWrap: "wrap",
					}}
				>
					<Typography
						sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.primary" }}
					>
						Inc%:
					</Typography>
					{[
						{
							label: "Hard",
							color: SHIFT_CONFIG_COLORS.greenHard,
							value: incThresholds.GreenHard,
						},
						{
							label: "Normal",
							color: SHIFT_CONFIG_COLORS.greenNormal,
							value: incThresholds.GreenNormal,
						},
						{
							label: "Soft",
							color: SHIFT_CONFIG_COLORS.greenSoft,
							value: incThresholds.GreenSoft,
						},
						{
							label: "Yellow",
							color: SHIFT_CONFIG_COLORS.yellow,
							value: incThresholds.Yellow,
						},
						{
							label: "Orange",
							color: SHIFT_CONFIG_COLORS.orange,
							value: incThresholds.Orange,
						},
						{
							label: "Red",
							color: SHIFT_CONFIG_COLORS.red,
							value: incThresholds.Red,
						},
					].map((zone) => (
						<Box
							key={zone.label}
							sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
						>
							<Box
								sx={{
									width: 14,
									height: 14,
									borderRadius: "2px",
									backgroundColor: zone.color,
									border: `1px solid ${SURFACE.border}`,
									flexShrink: 0,
								}}
							/>
							<span>{`≥ ${zone.value}%`}</span>
						</Box>
					))}
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Box
							sx={{
								width: 14,
								height: 14,
								borderRadius: "2px",
								backgroundColor: SURFACE.border,
								border: `1px solid ${SURFACE.border}`,
								flexShrink: 0,
							}}
						/>
						<span>No data</span>
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};
